import intlTelInput, { Iti } from 'intl-tel-input';
import * as libphonenumber from 'libphonenumber-js';
import { IsObject } from './object';
import * as strings from './strings';

declare interface myWindow extends Window {
    readonly intlTelInputUtilsScript: string;
}
declare const window: myWindow;

interface opts {
    customPlaceholder: (string: string) => string;
}

let defaults: opts | undefined;

export function SetDefaults(opts: opts) {
    defaults = opts;
}

export class Phone {
    Iti: Iti;

    constructor(public Input: HTMLInputElement, opts?: opts) {
        if (defaults) {
            if (opts) {
                opts = { ...defaults, ...opts };
            } else {
                opts = defaults;
            }
        }
        this.Iti = intlTelInput(Input, {
            loadUtils: () => import(window.intlTelInputUtilsScript || '/intl-tel-input/build/js/utils.js'),
            dropdownContainer: document.body,
            separateDialCode: true,
            autoPlaceholder: 'aggressive',
            customPlaceholder: opts?.customPlaceholder ?? (string => `example: ${string}`),
            initialCountry: 'auto',
            geoIpLookup: function (callback) {
                fetch('https://ipapi.co/json').
                    then(res => res.json()).
                    then(data => callback(IsObject(data) && typeof data['country_code'] === 'string' ? data['country_code'] || 'us' : 'us')).
                    catch(() => callback('us'));
            },
        });

        this.Input.setAttribute('autocomplete', 'tel-national');

        const flagParent = this.Input.previousSibling as HTMLElement || null;
        if (flagParent) {
            flagParent.querySelector('.iti__selected-flag')?.setAttribute('tabindex', '-1');
        }

        const input = async () => {
            if (!this.Input.required && !this.Input.value.trim()) {
                this.Input.setCustomValidity('');
                return;
            }

            await this.Iti.promise;
            if (!this.Iti.getSelectedCountryData().iso2) {
                this.Input.setCustomValidity('Please select a country.');
            } else if (!this.Iti.isValidNumber()) {
                this.Input.setCustomValidity('Please enter a valid phone number.');
            } else {
                this.Input.setCustomValidity('');
            }
        };
        this.Input.addEventListener('input', input);
        void input();

        this.Input.addEventListener('countrychange', () => {
            void input();
            this.Input.dispatchEvent(new Event('clearinvalid'));
        });
    }

    async Format(): Promise<string> {
        await this.Iti.promise;
        return strings.TrimPrefix(this.Iti.getNumber(intlTelInput.utils?.numberFormat.RFC3966), 'tel:');
    }

    async Human(): Promise<string> {
        return Human(await this.Format());
    }
}

let fromCountry: libphonenumber.CountryCode = 'US';

export function SetFromCountry(countryCode2: string) {
    fromCountry = countryCode2 as libphonenumber.CountryCode;
}

export function Human(phoneNumber: string): string {
    try {
        const p = libphonenumber.parsePhoneNumber(phoneNumber);
        if (p.country === fromCountry) {
            return p.format('NATIONAL');
        }

        return p.format('IDD', { fromCountry });
    } catch (err) {
        console.error(err);
        return '';
    }
}

export function Format(phoneNumber: string): string {
    try {
        const p = libphonenumber.parsePhoneNumber(phoneNumber, 'US');
        return p.format('RFC3966');
    } catch (err) {
        console.error(err);
        return '';
    }
}